/**
 * MoveResizeModal — numeric x/y/w/h steppers, bounded to the 12-col grid.
 * The KEYBOARD alternative to mouse drag/resize (design brief A11Y) —
 * round-trips through the SAME useUpdateWidget mutation the mouse path's
 * batch layout PUT ultimately feeds into (no shadow state), and enforces the
 * SAME WIDGET_MIN_W/H floors (+ per-kind donut minH) DashboardGrid hands RGL,
 * so the keyboard path can't create sizes the mouse path forbids (clip floors
 * audit finding 9).
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { toast } from 'sonner';
import { useUpdateWidget } from '@/api/dashboardConfig';
import { MUTATION_ERROR_TOAST_ID } from '@/lib/toastIds';
import { FormModal } from '@/components/FormModal';
import { labelStyle, fieldStyle, btnPrimaryStyle, btnGhostStyle, errorTextStyle } from '@/components/ProjectForm';
import { WIDGET_MIN_W, widgetMinH } from './gridConstants';
import type { DashboardWidgetResponse } from '@/types/domain';

const GRID_COLS = 12;

export interface MoveResizeModalProps {
  widget: DashboardWidgetResponse;
  onClose: () => void;
  onSaved: () => void;
}

export function MoveResizeModal({ widget, onClose, onSaved }: MoveResizeModalProps) {
  const [x, setX] = useState(widget.gridX);
  const [y, setY] = useState(widget.gridY);
  const [w, setW] = useState(widget.gridW);
  const [h, setH] = useState(widget.gridH);
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateWidget();

  // Same floors as the RGL layout items (per-kind donut override included) —
  // widget.builtinRef is null for METRIC/TEXT/... kinds, which resolves to
  // the base floor.
  const minH = widgetMinH(widget.builtinRef);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (w < WIDGET_MIN_W || h < minH || x < 0 || y < 0 || x + w > GRID_COLS) {
      setError(`Width must be at least ${WIDGET_MIN_W} and height at least ${minH}, and x + width can't exceed ${GRID_COLS} columns.`);
      return;
    }
    setError(null);
    update.mutate(
      { id: widget.id, dashboardId: widget.dashboardId, payload: { title: widget.title, gridX: x, gridY: y, gridW: w, gridH: h } },
      {
        onSuccess: () => { toast.success('Widget position updated.'); onSaved(); },
        onError: () => toast.error("Couldn't update the widget's position.", { id: MUTATION_ERROR_TOAST_ID }),
      },
    );
  }

  function numberField(id: string, label: string, value: number, min: number, max: number | undefined, onChange: (v: number) => void) {
    return (
      <div style={{ flex: 1 }}>
        <label htmlFor={id} style={labelStyle}>{label}</label>
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          step={1}
          value={value}
          onChange={(e) => {
            const parsed = Math.round(Number(e.target.value));
            onChange(Number.isFinite(parsed) ? parsed : min);
          }}
          className="focus-ring"
          style={fieldStyle}
        />
      </div>
    );
  }

  return (
    <FormModal title="Move / resize widget" onClose={onClose} width={360}>
      <form onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          {numberField('mr-x', 'Column (x)', x, 0, GRID_COLS - 1, setX)}
          {numberField('mr-y', 'Row (y)', y, 0, undefined, setY)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          {numberField('mr-w', 'Width', w, WIDGET_MIN_W, GRID_COLS, setW)}
          {numberField('mr-h', 'Height', h, minH, undefined, setH)}
        </div>
        {error && <span style={errorTextStyle}>{error}</span>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="pressable focus-ring" style={btnGhostStyle}>Cancel</button>
          <button type="submit" disabled={update.isPending} className="pressable focus-ring" style={{ ...btnPrimaryStyle, opacity: update.isPending ? 0.6 : 1 }}>
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </FormModal>
  );
}
